import { findEssenceAbilityForSkill, findSkillCatalogEntry, getRecipeSystem, type CharacterEquipmentLoadout, type Equipment, type Skill } from '@sorrem/shared';
import type { UICoalescence, UIInventoryItem } from '@sorrem-client/types';
import { CrystalIcon, ItemCard } from '@sorrem-client/ui/components';
import { hasSkillTreeSkillIcon, SkillTreeSkillIcon } from '@sorrem-client/ui/features/SkillTree';
import { describeAbilityFacts } from './ability-facts';
import styles from './ActionSlotSummary.module.scss';

interface SkillActionSummaryProps {
    skillId: string;
    /** The character's skills and wielded gear, which change the ability's cost and cooldown. */
    skills?: readonly Skill[];
    equipped?: CharacterEquipmentLoadout;
}

/** What an action slot does: a skill's Essence ability, at this character's cost and cooldown. */
export function SkillActionSummary({ skillId, skills, equipped }: SkillActionSummaryProps): JSX.Element {
    const ability = findEssenceAbilityForSkill(skillId);
    const skillName = findSkillCatalogEntry(skillId)?.name ?? skillId;
    const title = ability?.name ?? skillName;

    return (
        <section className={styles.actionSlotSummary} aria-label={title}>
            <header className={styles.header}>
                {hasSkillTreeSkillIcon(skillId) && (
                    <span className={styles.icon} aria-hidden='true'>
                        <SkillTreeSkillIcon skillId={skillId} />
                    </span>
                )}
                <div className={styles.titles}>
                    <h3 className={styles.name}>{title}</h3>
                    <p className={styles.kind}>{ability !== undefined && ability.name !== skillName ? `Essence ability - ${skillName}` : 'Essence ability'}</p>
                </div>
            </header>
            {ability === undefined
                ? <p className={styles.note}>This skill's ability is still being designed.</p>
                : (
                    <>
                        <p className={styles.description}>{ability.description}</p>
                        <dl className={styles.facts}>
                            {describeAbilityFacts(ability, skills, equipped).map((fact) => (
                                <div key={fact.label} className={styles.fact}>
                                    <dt>{fact.label}</dt>
                                    <dd>{fact.value}</dd>
                                </div>
                            ))}
                        </dl>
                    </>
                )}
        </section>
    );
}

interface CrystalActionSummaryProps {
    /** The crystal stack in the linked carried slot; undefined while its first crystal is still forming. */
    item: UIInventoryItem | undefined;
    /** Coalescences headed for the slot, soonest first. */
    pending: readonly UICoalescence[];
}

/** What an action slot does: use a crystal from its linked carried slot. */
export function CrystalActionSummary({ item, pending }: CrystalActionSummaryProps): JSX.Element | null {
    const forming = pending[0];
    const recipeId = item?.crystal?.recipeId ?? forming?.recipeId;
    const recipe = recipeId === undefined ? undefined : getRecipeSystem().getRecipe(recipeId);
    const crystalType = item?.crystal?.crystalType ?? forming?.crystalType;
    const size = item?.crystal?.size ?? forming?.size;
    if (crystalType === undefined || size === undefined) {
        return null;
    }
    const name = item?.name ?? recipe?.name ?? 'Crystal';
    const description = item?.crystal?.description ?? recipe?.description;
    const count = item === undefined
        ? 'Coalescing'
        : pending.length > 0 ? `${item.qty} carried - ${pending.length} more coalescing` : `${item.qty} carried`;

    return (
        <section className={styles.actionSlotSummary} aria-label={name}>
            <header className={styles.header}>
                <span className={styles.icon} aria-hidden='true'>
                    <CrystalIcon crystalType={crystalType} size={size} ghost={item === undefined} />
                </span>
                <div className={styles.titles}>
                    <h3 className={styles.name}>{name}</h3>
                    <p className={styles.kind}>{count}</p>
                </div>
            </header>
            {description
                ? <p className={styles.description}>{description}</p>
                : <p className={styles.note}>This crystal has no effect yet.</p>}
        </section>
    );
}

interface EquipmentSummaryProps {
    item: UIInventoryItem;
    /** The equipped items its stats compare against (`resolveComparedEquipment`); none for a worn item. */
    compared?: readonly Equipment[];
}

/** A piece of equipment, worn or linked to an action slot, compared with what it would replace. */
export function EquipmentSummary({ item, compared }: EquipmentSummaryProps): JSX.Element {
    return (
        <div className={styles.equipment}>
            <ItemCard item={item} compared={compared} />
        </div>
    );
}
